import React, { type HTMLAttributes, useRef } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Block } from '@uncinc/react-drupal-core';
import { useSelector } from 'react-redux';

import { Container, useWindowDimensions } from '@uncinc/uncinc-react-kitchen-sink';
import type { ImmutableEntity } from '@uncinc/react-drupal-core/types';

import {
  WysiwygField,
  Breadcrumbs,
  ButtonWrapper,
  Share,
  Divider,
} from '../../../components';
import { SCREEN_TABLET, REQUEST_TYPE_MAATJE_WORDEN, APP_OPTIONS } from '../../../config/constants';
import type { NodeMaatjesProject as Type } from '../../../types/uncinc_profile/nodes';

import './style.scss';


interface HomePageProps extends React.HTMLProps<HTMLAttributes<HTMLDivElement>> {
  entity: ImmutableEntity<Type>;
}

const NodeProjectMaatjes: React.FC<HomePageProps> = ({
  entity,
  className,
}) => {
  const { t } = useTranslation();
  const ctaRef = useRef<HTMLElement | null>(null);

  const requestType = useSelector((state: any) => state.getIn(['router', 'location', 'query', 'type'], REQUEST_TYPE_MAATJE_WORDEN));
  const { width } = useWindowDimensions();
  const isLarge = width > SCREEN_TABLET;
  const classes = classNames(className, 'NodeProjectMaatjes');
  const project = entity.toJS();
  const title = project.title[0].value;
  const body = project.body[0]?.processed;
  const bodySecondary = project.field_body_secondary[0]?.processed;
  const municipality = project.field_municipality[0]?.value;
  const find = project.field_find[0]?.value;
  const targetAudienceAge = project.field_target_audience_age[0]?.value;
  const activityDescription = project.field_activity_description[0]?.value;
  const organization = project.field_organization[0]?.value;
  const projectType = project.field_project_type[0]?.value;
  const weeklyTimeSpent = project.field_weekly_time_spent[0]?.value;
  const salesforceRecordId = project.field_salesforce_record_id[0]?.value;

  const scrollToCTA = () => {
    ctaRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  const ctaBlockID = requestType === REQUEST_TYPE_MAATJE_WORDEN
    ? APP_OPTIONS.parameters.ctaBlockMaatjeWorden
    : APP_OPTIONS.parameters.ctaBlockMaatjeZoeken;

  return (
    // eslint-disable-next-line react/no-unknown-property
    <article typeof="schema:WebPage" className={classes}>
      <div>
        <Container>
          <div className="row">
            <div className="col-12 col-lg-8 offset-lg-2">
              <Breadcrumbs includeCurrent={isLarge} hideHome={!isLarge} customCrumbs={null} />
              <div className="NodeProjectMaatjes__title">
                <h1>{title}</h1>
              </div>
              <div className="NodeProjectMaatjes__intro">
                {(organization || projectType || weeklyTimeSpent) && (
                  <div className="NodeProjectMaatjes__intro__specifics NodeProjectMaatjes__intro__specifics__part1">
                    {organization && (
                      <p>
                        {`${t('maatjes.project.organization')}: `}
                        <span>{organization}</span>
                      </p>
                    )}
                    {projectType && (
                      <p>
                        {`${t('maatjes.project.project_type')}: `}
                        <span>{projectType}</span>
                      </p>
                    )}
                    {weeklyTimeSpent && (
                      <p>
                        {`${t('maatjes.project.weekly_time_spend')}: `}
                        <span>{weeklyTimeSpent}</span>
                      </p>
                    )}
                  </div>
                )}
                {((organization || projectType || weeklyTimeSpent) &&
                  (municipality || targetAudienceAge || find)) && (<Divider className="NodeProjectMaatjes__intro__divider" />)}
                {(municipality || targetAudienceAge || find) && (
                  <div className="NodeProjectMaatjes__intro__specifics NodeProjectMaatjes__intro__specifics__part2">
                    {municipality && (
                      <p>
                        {`${t('maatjes.project.municipality')}: `}
                        <span>{municipality}</span>
                      </p>
                    )}
                    {targetAudienceAge && (
                      <p>
                        {`${t('maatjes.project.target_audience_age')}: `}
                        <span>{targetAudienceAge}</span>
                      </p>
                    )}
                    {find && (
                      <p>
                        {`${t('maatjes.project.find')}: `}
                        <span>{find}</span>
                      </p>
                    )}
                  </div>
                )}
              </div>
              <div className="NodeProjectMaatjes__buttons">
                <ButtonWrapper onClick={scrollToCTA} iconName="arrow_downward">{t('maatjes.project.button')}</ButtonWrapper>
                <Share title={t('share')} items={['facebook', 'copy']} />
              </div>
              <WysiwygField
                property="schema:text"
                className="NodeProjectMaatjes__body"
                html={requestType === REQUEST_TYPE_MAATJE_WORDEN ? body : bodySecondary}
              />
              {activityDescription && (
                <div className="NodeProjectMaatjes__activity_description">
                  <h4>{t('maatjes.project.activity_description')}</h4>
                  <p>{activityDescription}</p>
                </div>
              )}

              {/* CTA block */}
              <section ref={ctaRef}>
                <Block
                  id={ctaBlockID}
                  newBlock
                  leftAlign
                  additonalLinkParams={`?projectCode=${salesforceRecordId}&organisationName=${organization}`}
                />
              </section>
            </div>
          </div>
        </Container>
      </div>
    </article>
  );
};

export default NodeProjectMaatjes;
